// BranchPage.tsx - İyileştirilmiş versiyon
import React, {FC, useEffect, useState, useCallback} from 'react'
import {Form, Formik, FormikProps} from 'formik'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {IBranchModel, initialBranch, RELATED_OFFICE, validationBranch} from './BranchModel'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {createBranch, deleteBranch, loadBranches, updateBranch} from './Api'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import {YES_OR_NO} from '../../../../shared/models/SharedConstantLookups'
import TextAreaInput from '../../../../shared/components/TextAreaInput'
import PhoneInput from '../../../../shared/components/PhoneInput'
import {WORKING_TIMES} from '../location/LocationModel'
import {BranchTable} from './BranchTable'

const BranchPage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [branches, setBranches] = useState<IBranchModel[]>([])
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [tab, setTab] = useState<string | 'form'>('form')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [error, setError] = useState<string | null>(null)
  const [pageSize, setPageSize] = useState(10)

  const tabOptions: TabOption<'form' | 'detail' | 'list'>[] = [
    {key: 'form', title: 'Şube Tanımlama', icon: 'bi bi-geo-alt'},
    {key: 'detail', title: 'Şube Ayrıntıları', icon: 'bi bi-geo-alt'},
    {key: 'list', title: 'Şube Listesi', icon: 'bi bi-list'},
  ]

  const fetchBranches = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true)
      setError(null)

      try {
        const response = await loadBranches(page, pageSize, sort, order)
        if (response.data.hasError) {
          throw new Error(response.data.message || 'Failed to fetch branches')
        }
        setBranches(response.data.payload.content)
        setTotalPages(response.data.payload.totalPages)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred')
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // İlk yükleme
  useEffect(() => {
    if (tab === 'list') {
      fetchBranches(0)
    }
  }, [tab, fetchBranches])

  const handleSave = async (values: IBranchModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateBranch({...values, id: selectedId})
        showSuccessToast('Şube başarıyla güncellendi')
      } else {
        await createBranch(values)
        showSuccessToast('Şube başarıyla oluşturuldu')
      }
      setCurrentPage(0)
      await fetchBranches(0)
      resetForm()
      setSelectedId(null)
      setTab('list')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = useCallback((branch: IBranchModel, formik: FormikProps<IBranchModel>) => {
    formik.setValues(branch)
    setSelectedId(branch.id)
    setTab('form')
  }, [])

  const handleDeleteClick = useCallback((branch: IBranchModel) => {
    setSelectedId(branch.id)
    setShowModal(true)
  }, [])

  const handleDelete = async () => {
    if (selectedId) {
      setLoading(true)
      try {
        await deleteBranch(selectedId)
        showSuccessToast('Şube başarıyla silindi')
        await fetchBranches(currentPage)
      } catch (err) {
        console.error(err)
        showErrorToast('Şube silinirken bir hata oluştu')
      } finally {
        setLoading(false)
        setShowModal(false)
        setSelectedId(null)
      }
    }
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchBranches(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchBranches]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchBranches(currentPage, column, order)
    },
    [currentPage, fetchBranches]
  )

  return (
    <>
      <Formik
        initialValues={initialBranch}
        validationSchema={validationBranch}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
            setTab('form')
          }

          return (
            <>
              <FormTabHeader
                formId='branchForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
              />
              <div className='card mb-5'> 
                {/* Form ve Detail Tabları */}
                {(tab === 'form' || tab === 'detail') && (
                  <Form id='branchForm' className='form card-body'>
                    {tab === 'form' && (
                      <>
                        <div className='row mb-6'>
                          <TextInput
                            name='branchName'
                            label='Şube İsim'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='webName'
                            label='Webdeki İsim'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                        <div className='row mb-6'>
                          <TextInput
                            name='branchInfo'
                            label='Şube Bilgisi'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='companyTitle'
                            label='Firma Ünvanı'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                        <div className='row mb-6'>
                          <TextInput name='city' label='Şehir' wrapperClassName='col-md-6' />
                          <TextInput name='district' label='İlçe' wrapperClassName='col-md-6' />
                        </div>
                        <div className='row mb-6'>
                          <PhoneInput
                            name='phone'
                            type='phone'
                            label='Telefon'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='email'
                            type='email'
                            label='Mail Adresi'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                        <div className='row mb-6'>
                          <SelectInput
                            name='status'
                            label='Durum'
                            wrapperClassName='col-md-6'
                            options={YES_OR_NO}
                          />
                          <SelectInput
                            name='relatedOffice'
                            label='İlgili Ofis'
                            wrapperClassName='col-md-6'
                            options={RELATED_OFFICE}
                          />
                        </div>
                        <div className='row mb-6'>
                          <TextInput
                            name='webReservationHour'
                            label='Web Rez. Öncesi Saat'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='serviceCommissionRate'
                            label='Hiz. Alınan Kom %'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                        <div className='row mb-6'>
                          <div className='col-md-6'>
                            <div className='mb-3'>
                              <label className='form-label'>
                                Enlem & Boylam <i className='bi bi-info-circle ms-1'></i>
                              </label>
                              <div className='d-flex gap-3'>
                                <input
                                  type='text'
                                  {...formik.getFieldProps('latitude')}
                                  className='form-control'
                                  placeholder='Enlem'
                                />
                                <input
                                  type='text'
                                  {...formik.getFieldProps('longitude')}
                                  className='form-control'
                                  placeholder='Boylam'
                                />
                                <button type='button' className='btn btn-light'>
                                  <i className='bi bi-geo-alt'></i>
                                </button>
                              </div>
                            </div>
                          </div>
                          <TextInput
                            name='rentalCommissionRate'
                            label='Kira Alınan Kom %'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                        <div className='row mb-6'>
                          <TextAreaInput name='address' label='Adres' wrapperClassName='col-md-6' />
                          <TextInput
                            name='reservationColor'
                            type='color'
                            label='Rezervasyon Rengi'
                            wrapperClassName='col-md-3'
                          />
                          <TextInput
                            name='isNotPickupBranch'
                            type='checkbox'
                            label='Alış Şubesi Olamaz'
                            wrapperClassName='col-md-3'
                          />
                        </div>
                        <div className='mt-10'>
                          <h5 className='mb-4'>Şubenin Çalışma Saatlerini Giriniz</h5>
                          {WORKING_TIMES.map(({label, startKey, endKey}) => (
                            <div className='row mb-3' key={label}>
                              <div className='col-md-3'>
                                <strong>{label}</strong>
                              </div>
                              <TextInput name={startKey} type='time' wrapperClassName='col-md-4' />
                              <TextInput name={endKey} type='time' wrapperClassName='col-md-4' />
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    {tab === 'detail' && (
                      <>
                        <div className='row mb-6'>
                          <TextInput
                            name='webOrder'
                            type='number'
                            label='Web Sıralama'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='contractFormat'
                            label='Sözleşme No Format'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                        <div className='row mb-6'>
                          <TextInput
                            name='webParkingId'
                            label='Web Otopark ID'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput
                            name='contractNo'
                            label='Sözleşme No'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                        <div className='row mb-6'>
                          <TextInput
                            name='dealerId'
                            label='Bayi Cari ID'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput name='cashNo' label='Nakit No' wrapperClassName='col-md-6' />
                        </div>
                        <div className='row mb-6'>
                          <TextInput
                            name='onlineReservationId'
                            label='Online Rez ID'
                            wrapperClassName='col-md-6'
                          />
                          <TextInput name='bankNo' label='Banka No' wrapperClassName='col-md-6' />
                        </div>

                        {[1, 3, 5, 7, 9].map((i) => (
                          <div className='row mb-6' key={i}>
                            <TextInput
                              name={`specialService${i}`}
                              label={`Şube Özel Ücretsiz Hizmet ${i}`}
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name={`specialService${i + 1}`}
                              label={`Şube Özel Ücretsiz Hizmet ${i + 1}`}
                              wrapperClassName='col-md-6'
                            />
                          </div>
                        ))}
                        <div className='row mb-6'>
                          <TextInput
                            name='branchImage'
                            type='file'
                            label='Resim Dosyası Seçiniz'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                        <div className='row mb-6'>
                          <SelectInput
                            name='oldOffice'
                            label='Eski Ofis'
                            wrapperClassName='col-md-6'
                            options={YES_OR_NO}
                          />
                          <TextInput
                            name='newBranchName'
                            label='Yeni Şube Bilgisi'
                            wrapperClassName='col-md-6'
                          />
                        </div>
                      </>
                    )}
                  </Form>
                )}
              </div>
              {/* Liste Tabı */}
              {tab === 'list' && (
                <div className='card'> 
                  <div className='card-body pt-0'>
                    <BranchTable
                      data={branches}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      onEdit={(branch) => handleEdit(branch, formik)}
                      onDelete={handleDeleteClick}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                      loading={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )
        }}
      </Formik>

      <ConfirmModal
        show={showModal}
        header='Silme Onayı'
        text='Bu şubeyi silmek istediğinize emin misiniz?'
        handleClose={() => {
          setShowModal(false)
          setSelectedId(null)
        }}
        applyOnClick={handleDelete}
      />

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export {BranchPage}
